import { AlertTriangle, Check, Minus, X } from 'lucide-react';
import type { PlaygroundGrade, PlaygroundMetrics, PlaygroundRetrieveMetrics } from '@repo/shared-types';
import { Badge } from '../ui/badge';
import { Markdown, compactMarkdownComponents } from '../ui/markdown';
import type { PlaygroundResult, PlaygroundRetrieveResult } from '../../lib/api';
import { cn } from '../../lib/utils';

/**
 * The two coverage numbers, side by side, because either one alone is misleading.
 *
 * `claimed` is how much of the arrival reached a project at all — under 100% is text falling to the
 * organization root. `assigned` sums the spans as handed out, so a paragraph given to three projects
 * counts three times and the figure goes over 100% — which is the cross-project duplication that put
 * a page named after one project inside another's tree. A run can be wrong in either direction while
 * looking fine in the other, so the pair is the reading and neither half is a summary.
 */
export function CoverageBar({ metrics }: { metrics: PlaygroundMetrics }) {
  const overAssigned = metrics.assignedPct > 100.5;
  const underClaimed = metrics.claimedPct < 99.5;

  return (
    <div className="grid grid-cols-2 gap-2">
      <Stat
        label="Claimed"
        value={`${metrics.claimedPct}%`}
        tone={underClaimed ? 'warn' : 'good'}
        detail={
          metrics.unclaimedChars > 0
            ? `${metrics.unclaimedChars.toLocaleString()} chars went to the organization root`
            : 'everything reached a project'
        }
      />
      <Stat
        label="Assigned"
        value={`${metrics.assignedPct}%`}
        tone={overAssigned ? 'warn' : 'good'}
        detail={
          metrics.overlapChars > 0
            ? `${metrics.overlapChars.toLocaleString()} chars given to more than one project`
            : 'no project shares another’s sentences'
        }
      />
    </div>
  );
}

export function RunMetrics({ metrics }: { metrics: PlaygroundMetrics }) {
  const tools = Object.entries(metrics.toolCalls).sort((a, b) => b[1] - a[1]);

  return (
    <div className="space-y-3">
      <CoverageBar metrics={metrics} />

      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 text-[10px] text-ink-faint">
        <Figure n={metrics.destinationCount} label={metrics.destinationCount === 1 ? 'project' : 'projects'} />
        {/* The projects it named and dropped. Invisible in the outcome, and usually the finding:
            "nothing matched" reads the same whether it named no project or named the right one at
            0.44, and those are opposite problems with opposite fixes. */}
        {metrics.rejectedCount > 0 && <Figure n={metrics.rejectedCount} label="under the floor" tone="warn" />}
        <Figure n={metrics.toolCallCount} label="tool calls" />
        {metrics.discardedChars > 0 && (
          <Figure n={metrics.discardedChars} label="chars discarded" format="compact" />
        )}
        <span>{(metrics.durationMs / 1000).toFixed(1)}s</span>
        {!metrics.confirmed && (
          <span className="text-warning-text">ran out of steps before settling</span>
        )}
        {metrics.fellBack && <span className="text-warning-text">fell back to the organization</span>}
      </div>

      {tools.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {tools.map(([tool, count]) => (
            <span
              key={tool}
              className="rounded bg-surface-muted px-1.5 py-0.5 font-mono text-[10px] text-ink-muted"
            >
              {tool}
              <span className="ml-1 tabular-nums text-ink-faint">×{count}</span>
            </span>
          ))}
        </div>
      )}

      {metrics.items.length > 1 && (
        <div className="space-y-1">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-ink-faint">Per item</p>
          {metrics.items.map((item) => (
            <div key={item.itemIndex} className="flex items-baseline gap-2 text-[10px]">
              <span className="w-5 flex-shrink-0 tabular-nums text-ink-faint">{item.itemIndex}.</span>
              <span className="min-w-0 flex-1 truncate text-ink-muted">{item.title ?? 'untitled'}</span>
              <span
                className={cn(
                  'flex-shrink-0 tabular-nums',
                  item.claimedPct < 99.5 ? 'text-warning-text' : 'text-ink-faint',
                )}
              >
                {item.claimedPct}% claimed
              </span>
              <span
                className={cn(
                  'flex-shrink-0 tabular-nums',
                  item.assignedPct > 100.5 ? 'text-warning-text' : 'text-ink-faint',
                )}
              >
                {item.assignedPct}% assigned
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function GradePanel({ grade }: { grade: PlaygroundGrade }) {
  if (!grade.graded) {
    return (
      <p className="text-[11px] leading-relaxed text-ink-faint">
        Nothing was asserted about this run, so it is measured but not judged. Give the case an
        expectation to turn it into a test.
      </p>
    );
  }

  return (
    <div className="space-y-1.5">
      {grade.checks.map((check) => (
        <div key={check.id} className="flex items-start gap-2">
          {check.passed ? (
            <Check size={12} className="mt-0.5 flex-shrink-0 text-emerald-500" />
          ) : (
            <X size={12} className="mt-0.5 flex-shrink-0 text-danger-text" />
          )}
          <div className="min-w-0">
            <p className={cn('text-[11px]', check.passed ? 'text-ink-secondary' : 'text-ink')}>{check.label}</p>
            <p className="text-[10px] leading-relaxed text-ink-faint">{check.detail}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Where it put things, and — the part worth reading first — where it nearly did.
 *
 * `rejected` is the only evidence for whether the confidence floor is set anywhere near right, and
 * it is not visible anywhere else: a run that named the correct project at 0.44 and placed nothing
 * is recorded identically to one that found no candidate at all.
 */
export function DecisionPanel({ result }: { result: PlaygroundResult }) {
  return (
    <div className="space-y-3">
      {result.destinations.length === 0 ? (
        <p className="flex items-start gap-1.5 rounded-md border border-warning-ring bg-warning-bg px-2.5 py-1.5 text-[11px] text-warning-text">
          <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
          Nothing was placed. The whole arrival would have gone to the organization root.
        </p>
      ) : (
        <div className="space-y-1.5">
          {result.destinations.map((destination) => (
            <div key={destination.projectId} className="rounded-lg border border-border bg-card px-3 py-2">
              <div className="flex items-center gap-2">
                <span className="truncate text-[11px] font-medium text-ink">{destination.projectName}</span>
                {destination.fellBack && <Badge variant="warning">fallback</Badge>}
                <span className="ml-auto flex-shrink-0 text-[10px] tabular-nums text-ink-faint">
                  {Math.round(destination.confidence * 100)}% ·{' '}
                  {destination.parts.reduce((sum, part) => sum + part.chars, 0).toLocaleString()} chars
                </span>
              </div>
              <p className="mt-0.5 text-[10px] leading-relaxed text-ink-muted">{destination.reason}</p>
              {destination.parts.length > 0 && (
                <p className="mt-1 truncate text-[10px] text-ink-faint">
                  {destination.parts.map((part) => part.title).filter(Boolean).join(' · ')}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {result.rejected.length > 0 && (
        <div>
          <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-ink-faint">
            Named, but under the floor
          </p>
          <div className="space-y-1">
            {result.rejected.map((rejected, index) => (
              <div key={index} className="flex items-baseline gap-2 text-[10px]">
                <Minus size={10} className="mt-0.5 flex-shrink-0 text-ink-faint" />
                <span className="flex-shrink-0 font-medium text-ink-secondary">{rejected.projectName}</span>
                <span className="flex-shrink-0 tabular-nums text-warning-text">
                  {Math.round(rejected.confidence * 100)}%
                </span>
                <span className="min-w-0 flex-1 truncate text-ink-faint">
                  {rejected.topic} — {rejected.reason}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {result.discards.length > 0 && (
        <div>
          <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-ink-faint">Thrown away</p>
          {result.discards.map((discard, index) => (
            <p key={index} className="text-[10px] leading-relaxed text-ink-faint">
              {discard.chars.toLocaleString()} chars — {discard.reason}
            </p>
          ))}
        </div>
      )}

      {result.reasoning && (
        <div>
          <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-ink-faint">In its own words</p>
          <p className="text-[11px] leading-relaxed text-ink-muted">{result.reasoning}</p>
        </div>
      )}

      {(result.notes.length > 0 || result.roster.unknown.length > 0) && (
        <div className="space-y-0.5">
          {result.notes.map((note, index) => (
            <p key={index} className="text-[10px] text-warning-text">
              {note}
            </p>
          ))}
          {result.roster.unknown.length > 0 && (
            <p className="text-[10px] text-warning-text">
              this config names {result.roster.unknown.join(', ')}, which the agent no longer has — those
              settings did nothing
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/** A retrieval run's numbers: whether the loop answered itself, what it cited, what it opened. */
export function RetrieveRunMetrics({ metrics }: { metrics: PlaygroundRetrieveMetrics }) {
  const tools = Object.entries(metrics.toolCalls).sort((a, b) => b[1] - a[1]);
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <Stat
          label="Answered"
          value={metrics.answered ? 'loop' : 'fallback'}
          tone={metrics.answered ? 'good' : 'warn'}
          detail={
            metrics.answered
              ? `kb_answer${metrics.confidence !== null ? ` at ${Math.round(metrics.confidence * 100)}% confidence` : ''}`
              : 'the loop ended without kb_answer; the deterministic fallback wrote this'
          }
        />
        <Stat
          label="Cited"
          value={String(metrics.cited.length)}
          tone={metrics.cited.length > 0 ? 'good' : 'warn'}
          detail={`${metrics.visitedCount} opened · ${metrics.evidenceCited} source sentence${metrics.evidenceCited === 1 ? '' : 's'} cited`}
        />
      </div>
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 text-[10px] text-ink-faint">
        <Figure n={metrics.toolCallCount} label="tool calls" />
        <Figure n={metrics.answerChars} label="chars answered" format="compact" />
        <span>{(metrics.durationMs / 1000).toFixed(1)}s</span>
      </div>
      {tools.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {tools.map(([tool, count]) => (
            <span key={tool} className="rounded bg-surface-muted px-1.5 py-0.5 font-mono text-[10px] text-ink-muted">
              {tool}
              <span className="ml-1 tabular-nums text-ink-faint">×{count}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/** The answer as the asker would have received it, with what it rests on. */
export function AnswerPanel({ result }: { result: PlaygroundRetrieveResult }) {
  return (
    <div className="space-y-3">
      <p className="text-[11px] font-medium text-ink">{result.question}</p>
      {result.answer ? (
        <div className="rounded-lg border border-border bg-card px-3 py-2 text-xs">
          <Markdown components={compactMarkdownComponents}>{result.answer}</Markdown>
        </div>
      ) : (
        <p className="text-[11px] text-warning-text">No answer was produced.</p>
      )}
      {result.citations.length > 0 && (
        <div>
          <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-ink-faint">Cited</p>
          <div className="space-y-1">
            {result.citations.map((citation) => (
              <div key={citation.index} className="text-[10px]">
                <span className="tabular-nums text-ink-faint">[{citation.index}]</span>{' '}
                <span className="font-medium text-ink-secondary">{citation.title}</span>{' '}
                <span className="font-mono text-ink-faint">{citation.path}</span>
                {citation.evidence?.map((item, index) => (
                  <p key={index} className="ml-4 leading-relaxed text-ink-faint">
                    “{item.sourceQuote}” — {item.source}
                    {item.capturedAt ? `, ${item.capturedAt.slice(0, 10)}` : ''}
                  </p>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
      {result.visitedPaths.length > result.citations.length && (
        <p className="text-[10px] leading-relaxed text-ink-faint">
          Also opened: {result.visitedPaths.filter((p) => !result.citations.some((c) => p.endsWith(c.path))).join(', ')}
        </p>
      )}
      {result.roster.unknown.length > 0 && (
        <p className="text-[10px] text-warning-text">
          this config names {result.roster.unknown.join(', ')}, which the agent no longer has — those settings did
          nothing
        </p>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  detail,
  tone,
}: {
  label: string;
  value: string;
  detail: string;
  tone: 'good' | 'warn';
}) {
  return (
    <div
      className={cn(
        'rounded-lg border px-3 py-2',
        tone === 'warn' ? 'border-warning-ring bg-warning-bg' : 'border-border bg-surface-subtle',
      )}
    >
      <div className="flex items-baseline gap-1.5">
        <span
          className={cn(
            'text-base font-semibold tabular-nums',
            tone === 'warn' ? 'text-warning-text' : 'text-ink',
          )}
        >
          {value}
        </span>
        <span className="text-[10px] font-medium uppercase tracking-wide text-ink-faint">{label}</span>
      </div>
      <p className={cn('mt-0.5 text-[10px] leading-relaxed', tone === 'warn' ? 'text-warning-text' : 'text-ink-faint')}>
        {detail}
      </p>
    </div>
  );
}

function Figure({
  n,
  label,
  tone,
  format,
}: {
  n: number;
  label: string;
  tone?: 'warn';
  format?: 'compact';
}) {
  return (
    <span className={tone === 'warn' ? 'text-warning-text' : undefined}>
      <span className="font-medium tabular-nums">
        {format === 'compact' && n >= 1000 ? `${Math.round(n / 100) / 10}k` : n.toLocaleString()}
      </span>{' '}
      {label}
    </span>
  );
}
